// ============================================================
// calendarGrid — Day / Week / Month arithmetic for the calendar views.
//
// Shared by the web Calendar page (frontend/src/utils/calendarGrid.js
// re-exports it) and the iOS Calendar screens. Pure date arithmetic on LOCAL
// dates — never through a Graph dateTime, which both clients read as
// wall-clock digits.
// ============================================================

const pad2 = (n: number) => String(n).padStart(2, "0");
export const calendarIsoDate = (d: Date): string => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
export const calendarAddDays = (d: Date, n: number): Date => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

export type CalendarView = "day" | "week" | "workweek" | "month" | "agenda";

/** Monday of the week containing `d`, at local midnight. */
export function startOfWeek(d: Date): Date {
  const x = new Date(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  x.setHours(0, 0, 0, 0);
  return x;
}

/** The 6 × 7 month grid, Monday-first, always six rows (Outlook does the same). */
export function monthGrid(anchor: Date): { start: Date; end: Date; days: { date: Date; key: string; inMonth: boolean }[] } {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const start = startOfWeek(first);
  const days: { date: Date; key: string; inMonth: boolean }[] = [];
  for (let i = 0; i < 42; i += 1) {
    const d = calendarAddDays(start, i);
    days.push({ date: d, key: calendarIsoDate(d), inMonth: d.getMonth() === anchor.getMonth() });
  }
  return { start, end: calendarAddDays(start, 42), days };
}

/** The window each view loads: [start, end) at local midnight. */
export function viewRange(view: string, anchor: Date): { start: Date; end: Date } {
  const day = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
  if (view === "day") return { start: day, end: calendarAddDays(day, 1) };
  if (view === "week") { const s = startOfWeek(day); return { start: s, end: calendarAddDays(s, 7) }; }
  if (view === "workweek") { const s = startOfWeek(day); return { start: s, end: calendarAddDays(s, 5) }; }
  if (view === "month") { const g = monthGrid(day); return { start: g.start, end: g.end }; }
  return { start: day, end: calendarAddDays(day, 14) }; // agenda
}

/** How far Prev/Next moves the anchor for a view. */
export function stepAnchor(view: string, anchor: Date, dir: number): Date {
  if (view === "day") return calendarAddDays(anchor, dir);
  if (view === "week" || view === "workweek") return calendarAddDays(anchor, 7 * dir);
  if (view === "month") return new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1);
  return calendarAddDays(anchor, 14 * dir);
}

/**
 * Where a timed event sits in a day column, in minutes from midnight, from its
 * wall-clock start/end strings. Past midnight is clipped to the drawn day; a
 * zero-length event gets 15 minutes so it can be tapped.
 */
export function dayPlacement(startDateTime: string | null | undefined, endDateTime: string | null | undefined, dayKey: string): { top: number; height: number } | null {
  const parse = (s: string | null | undefined) => {
    const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/.exec(s || "");
    return m ? { day: m[1], mins: +m[2] * 60 + +m[3] } : null;
  };
  const s = parse(startDateTime);
  const e = parse(endDateTime);
  if (!s) return null;
  const top = s.day < dayKey ? 0 : (s.day > dayKey ? null : s.mins);
  if (top === null) return null;
  let bottom: number | null = !e ? top + 30 : (e.day > dayKey ? 1440 : (e.day < dayKey ? null : e.mins));
  if (bottom === null) return null;
  if (bottom <= top) bottom = Math.min(1440, top + 15);
  return { top, height: bottom - top };
}

/** Side-by-side columns for overlapping events — each item gains `col` and `cols`. */
export function layoutOverlaps<T extends { top: number; height: number }>(items: T[]): (T & { col: number; cols: number })[] {
  const sorted = [...items].sort((a, b) => a.top - b.top || b.height - a.height);
  const out: (T & { col: number; cols: number })[] = [];
  let group: (T & { col: number; cols: number })[] = [];
  let groupEnd = -1;
  const flush = () => {
    const cols: number[] = [];
    for (const it of group) {
      let c = cols.findIndex((end) => end <= it.top);
      if (c === -1) { c = cols.length; cols.push(0); }
      cols[c] = it.top + it.height;
      it.col = c;
    }
    for (const it of group) { it.cols = cols.length; out.push(it); }
    group = [];
  };
  for (const it of sorted) {
    if (group.length && it.top >= groupEnd) { flush(); groupEnd = -1; }
    group.push({ ...it, col: 0, cols: 1 });
    groupEnd = Math.max(groupEnd, it.top + it.height);
  }
  if (group.length) flush();
  return out;
}

export const REMINDER_CHOICES: { value: string; label: string }[] = [
  { value: "", label: "None" },
  { value: "0", label: "At start time" },
  { value: "5", label: "5 minutes before" },
  { value: "15", label: "15 minutes before" },
  { value: "30", label: "30 minutes before" },
  { value: "60", label: "1 hour before" },
  { value: "120", label: "2 hours before" },
  { value: "1440", label: "1 day before" },
  { value: "10080", label: "1 week before" },
];
